import { useEffect, useState } from "react";
import { X, History, Save, RotateCcw, Trash2, AlertTriangle } from "lucide-react";
import { useSheets } from "../modal/SheetsContext";

interface Props {
  onClose: () => void;
}

const PURGE_OPTIONS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "180 days", days: 180 },
  { label: "1 year", days: 365 },
];

function formatTimestamp(iso: string | null): string {
  if (!iso) return "Unknown time";
  return new Date(iso).toLocaleString([], {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export default function VersionHistoryModal({ onClose }: Props) {
  const { currentSheet, checkpoints, loadCheckpoints, saveCheckpoint, removeCheckpoint, purgeCheckpoints, restoreCheckpoint, hasUnsavedChanges } = useSheets();
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [purging, setPurging] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCheckpoints();
    // one modal instance is scoped to the sheet already loaded into context for its whole lifetime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSave() {
    // A checkpoint snapshots the server's last-saved rows — with auto-save
    // off, a not-yet-saved edit would silently be excluded, and restoring
    // this checkpoint later would revert to the stale pre-edit value with
    // no indication the edit was ever missing from it.
    if (hasUnsavedChanges && !window.confirm(
      "You have unsaved changes that are not saved yet. This version will not include them — save a version anyway?"
    )) return;
    setSaving(true);
    setError(null);
    try {
      await saveCheckpoint(label.trim() ? label.trim() : null);
      setLabel("");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save version");
    } finally {
      setSaving(false);
    }
  }

  async function handleRestore(checkpointId: number, checkpointLabel: string | null) {
    const name = checkpointLabel || formatTimestamp(checkpoints.find((c) => c.id === checkpointId)?.created_at ?? null);
    // The automatic safety checkpoint below only snapshots the DB's
    // last-saved rows — it has no way to capture this tab's in-memory,
    // not-yet-saved edits (auto-save off), so restoring would discard
    // those with no recovery path at all unless called out explicitly here.
    const unsavedWarning = hasUnsavedChanges
      ? " You also have unsaved edits in this sheet that are NOT saved anywhere — restoring will discard them permanently."
      : "";
    if (!window.confirm(`Restore "${name}"? This replaces every current column and row with this version's — a checkpoint of the current state is saved automatically first, so this can itself be undone.${unsavedWarning}`)) {
      return;
    }
    setRestoringId(checkpointId);
    setError(null);
    try {
      await restoreCheckpoint(checkpointId);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to restore version");
    } finally {
      setRestoringId(null);
    }
  }

  async function handleDelete(checkpointId: number, checkpointLabel: string | null) {
    const name = checkpointLabel || "this version";
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    setError(null);
    try {
      await removeCheckpoint(checkpointId);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to delete version");
    }
  }

  async function handlePurge(days: number, optionLabel: string) {
    if (!window.confirm(`Delete every saved version older than ${optionLabel}? This cannot be undone.`)) return;
    setPurging(days);
    setError(null);
    try {
      const deleted = await purgeCheckpoints(days);
      if (deleted === 0) window.alert(`No versions older than ${optionLabel} were found.`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to delete versions");
    } finally {
      setPurging(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <History className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold text-foreground font-inter">Version History — "{currentSheet?.name}"</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 mb-5 shrink-0">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Optional label (e.g. before Q3 cleanup)"
            className="flex-1 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 focus:outline-none focus:border-primary"
          />
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium rounded-xl font-inter transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Saving…" : "Save Version"}
          </button>
        </div>

        {error && (
          <p className="text-xs text-destructive font-inter mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 shrink-0">
            {error}
          </p>
        )}

        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          {checkpoints.length === 0 ? (
            <p className="text-xs text-muted-foreground font-inter">No saved versions yet.</p>
          ) : (
            <div className="space-y-1.5">
              {checkpoints.map((c) => (
                <div key={c.id} className="flex items-center justify-between bg-surface-muted/60 border border-border/60 rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-xs text-foreground font-inter truncate">{c.label || "Unlabeled version"}</p>
                    <p className="text-[11px] text-muted-foreground font-inter">
                      {formatTimestamp(c.created_at)} · {c.column_count} column(s), {c.row_count} row(s)
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <button
                      onClick={() => handleRestore(c.id, c.label)}
                      disabled={restoringId !== null}
                      title="Restore this version"
                      className="text-muted-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id, c.label)}
                      title="Delete this version"
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5 pt-4 border-t border-border shrink-0">
          <p className="text-[11px] font-medium text-muted-foreground font-inter mb-2">Delete versions older than…</p>
          <div className="flex items-center gap-1.5 flex-wrap">
            {PURGE_OPTIONS.map((opt) => (
              <button
                key={opt.days}
                onClick={() => handlePurge(opt.days, opt.label)}
                disabled={purging !== null || checkpoints.length === 0}
                className="flex items-center gap-1 px-2.5 py-1.5 border border-border hover:border-destructive/50 text-muted-foreground hover:text-destructive text-[11px] font-medium rounded-lg font-inter transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <AlertTriangle className="w-3 h-3" />
                {purging === opt.days ? "Deleting…" : opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end mt-5 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors font-inter"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
